import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";
import {
  BarChart3, Users, Eye, Calendar, Globe, Monitor, MousePointerClick,
  TrendingUp, MapPin,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const RANGES = [
  { k: 7, l: "7 días" },
  { k: 30, l: "30 días" },
  { k: 90, l: "90 días" },
  { k: 365, l: "1 año" },
];

const COLORS = ["#7a8e5a", "#b8c99a", "#e6ecd7", "#4d6a3a", "#a5b58e", "#d3dfbb", "#3d4a30", "#c6d3ac"];

function KpiCard({ icon: Icon, label, views, uniques, tint = "#e6ecd7" }) {
  return (
    <div className="bg-white border border-[#e6ecd7] rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-[#5c6b47] font-semibold">{label}</span>
        <span className="p-2 rounded-full" style={{ background: tint }}>
          <Icon className="w-4 h-4 text-[#4d6a3a]" />
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-3xl font-bold text-[#3d4a30]">{views.toLocaleString("es-ES")}</div>
        <div className="text-xs text-[#5c6b47]">visitas</div>
      </div>
      <div className="text-xs text-[#5c6b47] mt-1">
        <span className="font-semibold text-[#4d6a3a]">{uniques.toLocaleString("es-ES")}</span> únicos
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, right = null }) {
  return (
    <div className="bg-white border border-[#e6ecd7] rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#3d4a30] inline-flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#7a8e5a]" />} {title}
        </h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function PathList({ items, keyName = "path", valueName = "views", secondary = "uniques" }) {
  const max = Math.max(1, ...items.map((i) => i[valueName] || 0));
  if (!items.length) return <p className="text-sm text-[#5c6b47] italic">Sin datos aún.</p>;
  return (
    <div className="space-y-2">
      {items.map((row) => (
        <div key={row[keyName]} className="text-sm">
          <div className="flex justify-between items-baseline gap-3">
            <span className="text-[#3d4a30] truncate" title={row[keyName]}>{row[keyName] || "—"}</span>
            <span className="text-[#5c6b47] shrink-0 tabular-nums">
              {row[valueName].toLocaleString("es-ES")}
              {secondary && row[secondary] !== undefined && (
                <span className="text-[#a5b58e] ml-1">({row[secondary]})</span>
              )}
            </span>
          </div>
          <div className="h-1.5 bg-[#f4f7ec] rounded-full overflow-hidden mt-1">
            <div className="h-full rounded-full bg-[#7a8e5a]" style={{ width: `${(row[valueName] / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const COUNTRY_NAMES = {
  ES: "España", FR: "Francia", PT: "Portugal", IT: "Italia", DE: "Alemania",
  GB: "Reino Unido", US: "EE. UU.", MX: "México", AR: "Argentina", CL: "Chile",
  CO: "Colombia", PE: "Perú", VE: "Venezuela", UY: "Uruguay", EC: "Ecuador",
  BR: "Brasil", CA: "Canadá", NL: "Países Bajos", BE: "Bélgica", CH: "Suiza",
  MA: "Marruecos", AD: "Andorra", IE: "Irlanda", "NA": "Desconocido",
};

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr("");
    axios
      .get(`${API}/analytics/summary`, { params: { days }, withCredentials: true })
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch((e) => { if (!cancel) setErr(e?.response?.data?.detail || "Error cargando estadísticas"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [days]);

  const seriesFormatted = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((r) => ({
      ...r,
      label: new Date(r.day + "T00:00:00Z").toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
    }));
  }, [data]);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-cursive text-3xl text-[#4d6a3a] inline-flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Estadísticas del sitio
          </h2>
          <p className="text-sm text-[#5c6b47]">Analítica propia sin cookies · datos anónimos · retención 12 meses</p>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="analytics-range-picker">
          {RANGES.map((r) => (
            <button
              key={r.k}
              onClick={() => setDays(r.k)}
              data-testid={`range-${r.k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                days === r.k
                  ? "bg-[#7a8e5a] text-white"
                  : "bg-white border border-[#cddab6] text-[#3d4a30] hover:bg-[#f4f7ec]"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-[#5c6b47] italic">Cargando estadísticas…</p>}
      {err && <p className="text-red-600 text-sm">{err}</p>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
            <KpiCard icon={Calendar} label="Hoy" views={data.kpi.today.views} uniques={data.kpi.today.uniques} tint="#e6ecd7" />
            <KpiCard icon={TrendingUp} label="Últimos 7 días" views={data.kpi.last_7d.views} uniques={data.kpi.last_7d.uniques} tint="#d3dfbb" />
            <KpiCard icon={Users} label="Últimos 30 días" views={data.kpi.last_30d.views} uniques={data.kpi.last_30d.uniques} tint="#c6d3ac" />
            <KpiCard icon={Eye} label="Total histórico" views={data.kpi.total.views} uniques={data.kpi.total.uniques} tint="#b8c99a" />
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Card title={`Visitas por día · últimos ${days} días`} icon={TrendingUp}>
                <div className="h-72" data-testid="analytics-timeseries">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={seriesFormatted} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e6ecd7" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#5c6b47" }} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#5c6b47" }} />
                      <Tooltip
                        contentStyle={{ background: "#fff", border: "1px solid #cddab6", borderRadius: 8, fontSize: 12 }}
                        formatter={(v, n) => [v, n === "views" ? "Visitas" : "Únicos"]}
                        labelStyle={{ color: "#3d4a30", fontWeight: 600 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => (v === "views" ? "Visitas" : "Únicos")} />
                      <Line type="monotone" dataKey="views" stroke="#7a8e5a" strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="uniques" stroke="#4d6a3a" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            <Card title="Dispositivos" icon={Monitor}>
              <div className="h-56" data-testid="analytics-devices">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.devices}
                      dataKey="views"
                      nameKey="device"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                    >
                      {data.devices.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n, p) => [`${v} visitas`, p.payload.device]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Páginas más visitadas" icon={Eye}>
              <div data-testid="analytics-top-pages">
                <PathList items={data.top_pages} keyName="path" valueName="views" secondary="uniques" />
              </div>
            </Card>

            <Card title="Fuentes de tráfico" icon={Globe}>
              <div data-testid="analytics-referrers">
                <PathList
                  items={data.referrers.map((r) => ({ source: r.source || "direct", views: r.views }))}
                  keyName="source"
                  valueName="views"
                  secondary={null}
                />
              </div>
            </Card>

            <Card title="Países" icon={MapPin}>
              <div data-testid="analytics-countries">
                <PathList
                  items={data.countries.map((c) => ({
                    country: `${c.country} · ${COUNTRY_NAMES[c.country] || "—"}`,
                    views: c.views,
                  }))}
                  keyName="country"
                  valueName="views"
                  secondary={null}
                />
              </div>
              <p className="text-[10px] text-[#a5b58e] mt-3 italic">
                Detección aproximada por idioma del navegador (privacy-friendly).
              </p>
            </Card>

            <Card title="Navegadores" icon={Monitor}>
              <div className="h-56" data-testid="analytics-browsers">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.browsers} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e6ecd7" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#5c6b47" }} allowDecimals={false} />
                    <YAxis dataKey="browser" type="category" tick={{ fontSize: 11, fill: "#3d4a30" }} width={70} />
                    <Tooltip formatter={(v) => [`${v} visitas`, "Total"]} />
                    <Bar dataKey="views" fill="#7a8e5a" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="mt-4">
            <Card title="Eventos personalizados" icon={MousePointerClick} right={
              <span className="text-[10px] text-[#a5b58e]">clics donaciones · envíos contacto · aperturas galería</span>
            }>
              <div data-testid="analytics-custom-events">
                <PathList
                  items={data.custom_events.map((e) => ({ name: e.name || "—", count: e.count }))}
                  keyName="name"
                  valueName="count"
                  secondary={null}
                />
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
